import type jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { createPdf, downloadPdf } from '@/shared/pdf/createPdf';
import { formatMoney } from '@/shared/lib/money';
import logoUrl from '@/assets/LogoForni.png';
import type { ArFacturaData } from '../api';

// Layout constants (mm). A4 portrait = 210 × 297, 15 mm margins.
const MARGIN = 15;
const PAGE_W = 210;
const RIGHT = PAGE_W - MARGIN; // 195
const CENTER = PAGE_W / 2; // 105
const RED = '#CD212A'; // Italian red — brand accent
const RED_HEX = 'CD212A'; // same, sans '#', for the createPdf theme
const MUTED = 110;
const DARK = 30;

/** Load an asset URL into an <img> for jsPDF.addImage (browser-only). */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Comprobante letter (A/B/C/E) + AFIP código from the tipo string. Nota de
 *  Crédito uses cod 03/08/13; FCE MiPyMEs uses 201/206/211 (vs factura
 *  01/06/11). Exportación (E): factura 19, nota de crédito 21. */
function letterAndCod(tipo: string): { letra: string; cod: string } {
  const letra = (/([ABCE])\s*$/i.exec(tipo)?.[1] ?? 'B').toUpperCase();
  const esNC = /nota/i.test(tipo);
  if (letra === 'E') return { letra, cod: esNC ? '21' : '19' };
  const esFCE = /cr[eé]dito|mipyme/i.test(tipo);
  const cod = esNC
    ? letra === 'A'
      ? '03'
      : letra === 'C'
        ? '13'
        : '08'
    : esFCE
      ? letra === 'A'
        ? '201'
        : letra === 'C'
          ? '211'
          : '206'
      : letra === 'A'
        ? '01'
        : letra === 'C'
          ? '11'
          : '06';
  return { letra, cod };
}

/** Artículo cell text, with a serie/despacho sub-line for hornos (empty until
 *  the venta is dispatched). autotable renders the `\n` as a second line in the
 *  same cell — print-only detail the contadora needs (N° Despacho = pedimento). */
function artCell(l: { articulo: string; nroSerie?: string; nroDespacho?: string }): string {
  if (!l.nroSerie && !l.nroDespacho) return l.articulo;
  return `${l.articulo}\nN° Serie: ${l.nroSerie || '—'}  ·  N° Despacho: ${l.nroDespacho || '—'}`;
}

/** 'yyyy-mm-dd' / 'yyyymmdd' → 'dd/mm/yyyy' (formato impreso de los comprobantes); otro texto, igual. */
function fmtFecha(v: string | null | undefined): string {
  const m = /^(\d{4})-?(\d{2})-?(\d{2})$/.exec((v ?? '').trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (v ?? '');
}

/** es-AR number with 2 decimals, no currency symbol (for table cells). */
function fmtNum(n: number): string {
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}

/**
 * Build the AR comprobante PDF (jsPDF) after a CAE is issued. ARCA returns
 * no document, so the seller prints its own representación impresa with the
 * AFIP QR (RG 4291/18). Itemized "FACTURA DE VENTA" layout matching the
 * client's reference: 3-column header, receptor block, moneda row, a
 * per-line table (prorated server-side for partial cobros), an IVA-by-rate
 * breakdown, and the ARCA + QR footer. Shared by the download + preview +
 * auto-attach paths.
 */
async function buildFacturaArcaDoc(f: ArFacturaData): Promise<jsPDF> {
  const pdf = createPdf({ primary: RED_HEX, muted: '64748B' });
  const { doc } = pdf;
  const { letra, cod } = letterAndCod(f.tipoComprobante);
  // Factura B (Consumidor Final) y C (monotributo) NO discriminan IVA: los
  // precios se muestran con IVA incluido y sin columnas de alícuota. Factura B
  // suma al pie el "IVA Contenido" (Régimen de Transparencia Fiscal, Ley
  // 27.743). Factura A mantiene el desglose tradicional.
  // Factura E / NC E (exportación): importes en USD, sin IVA (operación no gravada), con
  // cotización, incoterm y permiso de embarque.
  const ex = f.exportacion;
  const cur = ex ? 'USD' : 'ARS';
  const noDiscriminaIva = letra === 'B' || letra === 'C' || ex != null;
  const esFacturaB = letra === 'B';

  /** label (bold) + value (normal) at (x, y); value at valueX or after label. */
  const fld = (label: string, value: string, x: number, y: number, valueX?: number): void => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(DARK);
    doc.text(label, x, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(DARK);
    const vx = valueX ?? x + doc.getTextWidth(label) + 1.5;
    doc.text(value || '', vx, y);
  };

  // ---- Header: logo + emisor (left) · letter box (center) · title + meta (right) ----
  try {
    const logo = await loadImage(logoUrl);
    const logoW = 46;
    const ratio = logo.naturalWidth > 0 ? logo.naturalHeight / logo.naturalWidth : 0.3;
    doc.addImage(logo, 'PNG', MARGIN, 12, logoW, logoW * ratio);
  } catch {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(RED);
    doc.text('Fornitalia', MARGIN, 20);
  }

  // Centered AFIP letter box (A/B/C) + código + emisor condición IVA.
  const boxS = 14;
  const boxY = 12;
  doc.setDrawColor(DARK);
  doc.setLineWidth(0.4);
  doc.roundedRect(CENTER - boxS / 2, boxY, boxS, boxS, 1, 1);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(DARK);
  doc.text(letra, CENTER, boxY + 9.5, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(MUTED);
  doc.text(`COD. ${cod}`, CENTER, boxY + boxS + 3, { align: 'center' });
  if (f.emisor.condicionIva) {
    doc.setFontSize(7);
    doc.setTextColor(DARK);
    doc.text(f.emisor.condicionIva, CENTER, boxY + boxS + 7, { align: 'center' });
  }

  // Right: title + comprobante meta block.
  const esNotaCredito = /nota/i.test(f.tipoComprobante);
  const esFce = !esNotaCredito && /cr[eé]dito|mipyme/i.test(f.tipoComprobante);
  const titulo = esNotaCredito
    ? 'NOTA DE CRÉDITO'
    : ex
      ? 'FACTURA DE EXPORTACIÓN'
      : esFce
        ? 'FACTURA DE CRÉDITO ELECTRÓNICA'
        : 'FACTURA DE VENTA';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(esFce ? 12 : 15);
  doc.setTextColor(RED);
  doc.text(titulo, RIGHT, 16, { align: 'right' });
  const metaLabelX = 132;
  const metaValueX = 156;
  let ry = 23;
  const metaRow = (label: string, value: string): void => {
    fld(label, value, metaLabelX, ry, metaValueX);
    ry += 4.6;
  };
  metaRow('Número:', f.nroComprobante);
  metaRow('Fecha:', fmtFecha(f.fecha));
  metaRow('CUIT:', f.emisor.cuit);
  metaRow('Ing. Brutos:', f.emisor.ingresosBrutos ?? '');
  metaRow('Inicio Act.:', f.emisor.inicioActividades ?? '');
  // Nota de Crédito: the factura it credits (AFIP CbtesAsoc).
  if (f.comprobanteAsociado) {
    metaRow('Comp. asoc.:', f.comprobanteAsociado.display);
  }

  // Left: emisor razón social + domicilio (under the logo).
  fld('Razón Social:', f.emisor.razonSocial, MARGIN, 33);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(DARK);
  doc.text('Domicilio:', MARGIN, 38);
  doc.setFont('helvetica', 'normal');
  // Wrap short of the right-hand meta block (starts at metaLabelX=132) so a
  // long domicilio doesn't clip into "Ing. Brutos:". Value starts at MARGIN+18.
  const domLines = doc.splitTextToSize(f.emisor.domicilio ?? '', metaLabelX - (MARGIN + 18) - 3);
  doc.text(domLines, MARGIN + 18, 38);

  // ---- Receptor ----
  let y = 50;
  doc.setDrawColor(200);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, RIGHT, y);
  y += 6;
  fld('Sr. (es):', f.receptor.nombre, MARGIN, y);
  fld(ex ? 'ID Impositivo:' : 'CUIT:', f.receptor.documento, metaLabelX, y, metaValueX);
  y += 6;
  fld('Domicilio:', f.receptor.domicilio ?? '', MARGIN, y);
  fld('Cond. IVA:', f.receptor.condicionIva ?? '', metaLabelX, y, metaValueX);
  y += 4;
  doc.setDrawColor(200);
  doc.line(MARGIN, y, RIGHT, y);

  // ---- Moneda / forma de pago row ----
  y += 6;
  fld('Moneda:', f.moneda, MARGIN, y);
  fld(ex ? 'País destino:' : 'Prov. Destino:', ex ? ex.paisDestino : (f.receptor.provincia ?? ''), 78, y);
  fld('Fecha Vto.:', fmtFecha(f.fechaVencimiento ?? f.fecha), metaLabelX, y, metaValueX);
  y += 5;
  fld('Forma de pago:', f.formaPago ?? '', metaLabelX, y, metaValueX);
  if (ex) {
    // Datos propios de la exportación: cotización declarada, Incoterm y permiso de embarque.
    fld(
      'Cotización:',
      `1 USD = $ ${fmtNum(ex.cotizacion)}`,
      MARGIN,
      y,
    );
    fld('Incoterm:', ex.incoterm, 78, y);
    y += 5;
    fld('CUIT país:', ex.cuitPais, MARGIN, y);
    fld('Permiso de embarque:', ex.permiso ?? 'Sin permiso', 78, y);
  }
  y += 3;
  doc.setDrawColor(200);
  doc.line(MARGIN, y, RIGHT, y);

  // ---- Line-item table (amounts already prorated server-side) ----
  pdf.y = y + 4;
  const tableStyles = {
    styles: { fontSize: 8, cellPadding: 1.6, textColor: DARK },
    headStyles: {
      fillColor: [238, 240, 242] as [number, number, number],
      textColor: DARK,
      halign: 'left' as const,
      fontStyle: 'bold' as const,
    },
    alternateRowStyles: { fillColor: [250, 250, 251] as [number, number, number] },
  };
  // Right-align the numeric-column HEADERS so they sit above their right-aligned
  // values (headStyles.halign is section-wide and columnStyles didn't override it
  // for the head row — so we style the head cells directly, which always wins).
  const rHead = (t: string): { content: string; styles: { halign: 'right' } } => ({
    content: t,
    styles: { halign: 'right' },
  });
  if (noDiscriminaIva) {
    // Factura B/C: sin columnas de IVA. Precio Unit. y Subtotal con IVA
    // incluido (Precio Unit. = importe c/IVA ÷ cantidad).
    const head = [
      'Cód.',
      'Artículo',
      rHead('Cantidad'),
      rHead('Precio Unit.'),
      rHead('% Dto.'),
      rHead('Subtotal'),
    ];
    const bodyRows = f.lineas.map((l) => {
      const precioConIva = l.cantidad > 0 ? l.importeConIva / l.cantidad : l.importeConIva;
      return [
        l.codigo,
        artCell(l),
        fmtNum(l.cantidad),
        fmtNum(precioConIva),
        fmtNum(l.descuentoPct),
        fmtNum(l.importeConIva),
      ];
    });
    pdf.table(head, bodyRows, {
      ...tableStyles,
      columnStyles: {
        0: { cellWidth: 20 },
        2: { halign: 'right', cellWidth: 22 },
        3: { halign: 'right', cellWidth: 34 },
        4: { halign: 'right', cellWidth: 18 },
        5: { halign: 'right', cellWidth: 36 },
      },
    });
  } else {
    const head = [
      'Cód.',
      'Artículo',
      rHead('Cantidad'),
      rHead('Precio'),
      rHead('% Dto.'),
      rHead('Importe'),
      rHead('Alíc. IVA'),
      rHead('Impt. c/IVA'),
    ];
    const bodyRows = f.lineas.map((l) => [
      l.codigo,
      artCell(l),
      fmtNum(l.cantidad),
      fmtNum(l.precioUnitario),
      fmtNum(l.descuentoPct),
      fmtNum(l.importe),
      `${fmtNum(l.ivaAlicuota)}%`,
      fmtNum(l.importeConIva),
    ]);
    pdf.table(head, bodyRows, {
      ...tableStyles,
      columnStyles: {
        0: { cellWidth: 18 },
        2: { halign: 'right', cellWidth: 16 },
        3: { halign: 'right', cellWidth: 24 },
        4: { halign: 'right', cellWidth: 14 },
        5: { halign: 'right', cellWidth: 26 },
        6: { halign: 'right', cellWidth: 16 },
        7: { halign: 'right', cellWidth: 28 },
      },
    });
  }

  // ---- Totals ----
  let ty = pdf.y + 2;
  if (ty > 250) {
    doc.addPage();
    ty = MARGIN + 8;
  }
  doc.setDrawColor(200);
  doc.line(MARGIN, ty, RIGHT, ty);
  ty += 5;
  if (noDiscriminaIva) {
    // Factura B/C: sin desglose de IVA. Solo el Importe Total; Factura B suma
    // la línea "IVA Contenido" (Régimen de Transparencia Fiscal, Ley 27.743).
    doc.setFillColor(245, 247, 249);
    doc.rect(MARGIN, ty, RIGHT - MARGIN, 9, 'F');
    ty += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(DARK);
    doc.text(`Importe Total: ${formatMoney(f.importes.total, cur)}`, RIGHT - 2, ty, {
      align: 'right',
    });
    ty += 8;
    if (ex) {
      // Exportación: no gravada. El equivalente en pesos es sólo informativo.
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(MUTED);
      doc.text(
        `Exportación — operación no gravada (sin IVA). Equivalente en pesos a la cotización declarada: ${formatMoney(ex.importePesos, 'ARS')}`,
        MARGIN,
        ty,
      );
      ty += 6;
    }
    if (esFacturaB) {
      doc.setDrawColor(200);
      doc.line(MARGIN, ty, RIGHT, ty);
      ty += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(DARK);
      doc.text('Régimen de Transparencia Fiscal al Consumidor (Ley 27.743)', MARGIN, ty);
      doc.setFont('helvetica', 'bold');
      doc.text(`IVA Contenido: ${formatMoney(f.importes.impuestos, 'ARS')}`, RIGHT, ty, {
        align: 'right',
      });
    }
  } else {
    const b = f.importes.ivaBuckets;
    const buckets: [string, number][] = [
      ['IVA 2.5:', b['2.5']],
      ['IVA 5:', b['5']],
      ['IVA 10.5:', b['10.5']],
      ['IVA 21:', b['21']],
      ['IVA 27:', b['27']],
    ];
    const colW = (RIGHT - MARGIN) / 5;
    doc.setFontSize(7.5);
    buckets.forEach(([label, val], i) => {
      const x = MARGIN + i * colW;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(DARK);
      doc.text(label, x, ty);
      doc.setFont('helvetica', 'normal');
      doc.text(fmtNum(val), x + doc.getTextWidth(label) + 1.5, ty);
    });
    ty += 4;
    // Bruto / Impuestos / Total band.
    doc.setFillColor(245, 247, 249);
    doc.rect(MARGIN, ty, RIGHT - MARGIN, 9, 'F');
    ty += 6;
    fld('Bruto:', fmtNum(f.importes.neto), MARGIN + 2, ty);
    fld('Impuestos:', fmtNum(f.importes.impuestos), 78, ty);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(DARK);
    doc.text(`Total: ${formatMoney(f.importes.total, 'ARS')}`, RIGHT - 2, ty, { align: 'right' });
  }
  ty += 8;

  // ---- ARCA footer: QR + authorization data. Pinned near the page bottom
  // (leaving whitespace above, like the reference) unless the totals reach
  // that far, in which case it flows just below them / onto a new page. ----
  let fy = 250;
  if (ty + 34 > 285) {
    doc.addPage();
    fy = MARGIN + 12;
  } else if (ty + 34 > fy) {
    fy = ty + 6;
  }
  doc.setDrawColor(DARK);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, fy - 5, RIGHT, fy - 5);
  // AFIP QR (RG 4291/18). Empty qrUrl means ARCA didn't return the número
  // yet (PENDIENTE) — render a note instead of a QR built on a 0 número.
  if (!f.qrUrl) {
    doc.setFontSize(6.5);
    doc.setTextColor(MUTED);
    doc.text('QR pendiente — verificá el número en ARCA.', MARGIN, fy + 6, { maxWidth: 32 });
  } else {
    try {
      const qrDataUrl = await QRCode.toDataURL(f.qrUrl, { margin: 1, width: 256 });
      doc.addImage(qrDataUrl, 'PNG', MARGIN, fy, 30, 30);
    } catch {
      doc.setFontSize(6);
      doc.setTextColor(MUTED);
      doc.text(f.qrUrl, MARGIN, fy + 4, { maxWidth: 32 });
    }
  }
  const tx = MARGIN + 36;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(DARK);
  doc.text('ARCA', tx, fy + 5);
  doc.setFontSize(9);
  doc.text('Comprobante Autorizado', tx, fy + 10.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(MUTED);
  doc.text(
    'Esta agencia no se responsabiliza por los datos ingresados en el detalle de la operación.',
    tx,
    fy + 15,
    { maxWidth: 90 },
  );
  // CAE block (right of the ARCA text).
  doc.setFontSize(9);
  fld('CAE:', f.cae || '—', 138, fy + 5);
  fld('Fecha Vto. CAE:', f.vencimiento ? fmtFecha(f.vencimiento) : '—', 138, fy + 11);

  return pdf.finalize();
}

/** Build + download the comprobante PDF in the browser. */
export async function downloadFacturaArcaPdf(f: ArFacturaData): Promise<void> {
  const doc = await buildFacturaArcaDoc(f);
  downloadPdf(doc, `factura-${f.nroComprobante || 'arca'}`);
}

/** Build the comprobante PDF as a File, ready to upload as the factura
 *  attachment (the same slot the manual upload uses). */
export async function buildFacturaArcaPdfFile(f: ArFacturaData): Promise<File> {
  const doc = await buildFacturaArcaDoc(f);
  const blob = doc.output('blob');
  return new File([blob], `factura-${f.nroComprobante || 'arca'}.pdf`, {
    type: 'application/pdf',
  });
}
